<template>
  <section class="tag-filter" aria-label="Explore Reckon Way stories">
    <div class="tag-filter-heading">
      <div>
        <p class="tag-filter-kicker">Explore By Experience</p>
        <h2>Find Stories</h2>
        <p class="tag-filter-intro">
          Filter every Reckon Way story by type and exact tags.
        </p>
      </div>
      <p class="tag-filter-result-count" role="status" aria-live="polite" aria-atomic="true">
        {{ resultSummary }}
      </p>
    </div>

    <button
      ref="mobileTrigger"
      type="button"
      class="tag-filter-mobile-trigger"
      aria-haspopup="dialog"
      :aria-expanded="mobileDialog.toString()"
      @click="openMobileFilters"
    >
      <span>
        <strong>Refine Stories</strong>
        <small>{{ mobileTriggerSummary }}</small>
      </span>
      <span class="mobile-trigger-action">
        <span v-if="activeFilterCount" class="mobile-filter-count">{{ activeFilterCount }}</span>
        <v-icon aria-hidden="true">mdi-tune-variant</v-icon>
      </span>
    </button>

    <v-dialog
      v-model="mobileDialog"
      :z-index="4000"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      content-class="story-filter-mobile-dialog"
    >
      <div class="story-filter-mobile-panel">
        <header class="mobile-filter-header">
          <div>
            <p>Explore The Reckon Way</p>
            <h2>Refine Stories</h2>
          </div>
          <button type="button" aria-label="Cancel filter changes" @click="cancelMobileFilters">
            <v-icon aria-hidden="true">mdi-close</v-icon>
          </button>
        </header>

        <div class="mobile-filter-content">
          <StoryFilterControls
            ref="mobileControls"
            :value="draftValue"
            :entries="entries"
            :tags="tags"
            @input="handleDraftChange"
          />
        </div>

        <footer class="mobile-filter-footer">
          <button type="button" class="mobile-filter-cancel" @click="cancelMobileFilters">
            Cancel
          </button>
          <button
            v-if="draftActiveFilterCount"
            type="button"
            class="mobile-filter-clear"
            @click="clearMobileFilters"
          >
            Clear All
          </button>
          <button type="button" class="mobile-filter-apply" @click="applyMobileFilters">
            Show {{ draftResultSummary }}
          </button>
        </footer>
      </div>
    </v-dialog>
  </section>
</template>

<script>
import StoryFilterControls from '@/components/StoryFilterControls.vue';
import searchUtilities from '@/lib/reckonWaySearch.cjs';

const { getFacetState } = searchUtilities;

function copyFilters(filters = {}) {
  return {
    kindSlugs: [...(filters.kindSlugs || [])],
    tagSlugs: [...(filters.tagSlugs || [])],
    matchMode: filters.matchMode === 'any' ? 'any' : 'all',
  };
}

function storyCountLabel(count) {
  return `${count} ${count === 1 ? 'story' : 'stories'}`;
}

export default {
  name: 'TagFilter',
  components: {
    StoryFilterControls,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    entries: {
      type: Array,
      required: true,
    },
    tags: {
      type: Array,
      required: true,
    },
    resultCount: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      draftValue: copyFilters(this.value),
      mobileDialog: false,
    };
  },
  computed: {
    appliedFacetState() {
      return getFacetState(this.entries, this.tags, this.value);
    },
    draftFacetState() {
      return getFacetState(this.entries, this.tags, this.draftValue);
    },
    appliedResultCount() {
      return Number.isFinite(this.resultCount)
        ? this.resultCount
        : this.appliedFacetState.resultCount;
    },
    resultSummary() {
      return storyCountLabel(this.appliedResultCount);
    },
    draftResultSummary() {
      return storyCountLabel(this.draftFacetState.resultCount);
    },
    activeFilterCount() {
      return this.appliedFacetState.filters.kindSlugs.length
        + this.appliedFacetState.filters.tagSlugs.length;
    },
    draftActiveFilterCount() {
      return this.draftFacetState.filters.kindSlugs.length
        + this.draftFacetState.filters.tagSlugs.length;
    },
    mobileTriggerSummary() {
      if (!this.activeFilterCount) return `Showing all ${this.resultSummary}`;

      return `${this.activeFilterCount} ${this.activeFilterCount === 1 ? 'filter' : 'filters'} · ${this.resultSummary}`;
    },
  },
  watch: {
    value: {
      deep: true,
      handler(nextValue) {
        if (!this.mobileDialog) this.draftValue = copyFilters(nextValue);
      },
    },
    mobileDialog(isOpen) {
      if (typeof document !== 'undefined') {
        document.body.classList.toggle('story-filter-dialog-open', isOpen);
      }
      if (!isOpen) this.draftValue = copyFilters(this.value);
    },
  },
  beforeDestroy() {
    if (typeof document !== 'undefined') {
      document.body.classList.remove('story-filter-dialog-open');
    }
  },
  methods: {
    handleDraftChange(nextValue) {
      this.draftValue = copyFilters(nextValue);
    },
    openMobileFilters() {
      this.draftValue = copyFilters(this.value);
      this.mobileDialog = true;
      this.$nextTick(() => {
        const dialog = document.querySelector('.story-filter-mobile-dialog');
        if (dialog) dialog.scrollTop = 0;
      });
    },
    closeMobileFilters() {
      this.mobileDialog = false;
      this.$nextTick(() => {
        if (this.$refs.mobileTrigger) this.$refs.mobileTrigger.focus();
      });
    },
    cancelMobileFilters() {
      this.draftValue = copyFilters(this.value);
      this.closeMobileFilters();
    },
    clearMobileFilters() {
      if (this.$refs.mobileControls) this.$refs.mobileControls.clearAll();
    },
    applyMobileFilters() {
      this.$emit('input', copyFilters(this.draftValue));
      this.closeMobileFilters();
    },
  },
};
</script>

<style scoped>
.tag-filter {
  background: #0b0b0b;
  border: 1px solid #292929;
  border-radius: 12px;
  margin: 0 auto 36px;
  max-width: 1100px;
  padding: 24px;
}

.tag-filter-heading {
  align-items: flex-start;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  margin-bottom: 20px;
}

.tag-filter-kicker,
.mobile-filter-header p {
  color: #e83b3b;
  font-family: "Oswald", sans-serif;
  font-size: .78rem;
  font-weight: 900;
  letter-spacing: .04em;
  margin: 0;
  text-transform: uppercase;
}

.tag-filter h2,
.mobile-filter-header h2 {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: 1.7rem;
  line-height: 1.1;
  margin: 5px 0 0;
  text-transform: uppercase;
}

.tag-filter-intro {
  color: #aaa;
  line-height: 1.5;
  margin: 8px 0 0;
}

.tag-filter-result-count {
  background: #151515;
  border: 1px solid #353535;
  border-radius: 999px;
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: .82rem;
  font-weight: 900;
  margin: 0;
  padding: 8px 12px;
  text-transform: uppercase;
  white-space: nowrap;
}

.tag-filter-mobile-trigger {
  align-items: center;
  background: #121212;
  border: 1px solid #343434;
  border-radius: 10px;
  color: #fff;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: 15px;
  text-align: left;
  width: 100%;
}

.tag-filter-mobile-trigger strong,
.tag-filter-mobile-trigger small {
  display: block;
}

.tag-filter-mobile-trigger strong {
  font-family: "Oswald", sans-serif;
  font-size: 1rem;
  text-transform: uppercase;
}

.tag-filter-mobile-trigger small {
  color: #aaa;
  margin-top: 3px;
}

.mobile-trigger-action {
  align-items: center;
  display: inline-flex;
  gap: 8px;
}

.mobile-trigger-action .v-icon {
  color: #fff;
}

.mobile-filter-count {
  align-items: center;
  background: #e83b3b;
  border-radius: 999px;
  display: inline-flex;
  font-family: "Oswald", sans-serif;
  font-size: .72rem;
  font-weight: 900;
  height: 24px;
  justify-content: center;
  min-width: 24px;
  padding: 0 6px;
}

.story-filter-mobile-panel {
  background: #050505;
  color: #fff;
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.mobile-filter-header,
.mobile-filter-footer {
  align-items: center;
  background: #090909;
  display: flex;
  justify-content: space-between;
  padding: 16px;
  position: sticky;
  z-index: 3;
}

.mobile-filter-header {
  border-bottom: 1px solid #292929;
  top: 0;
}

.mobile-filter-header button {
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 8px;
}

.mobile-filter-header .v-icon {
  color: #fff;
}

.mobile-filter-content {
  flex: 1;
  padding: 16px 16px 112px;
}

.mobile-filter-footer {
  border-top: 1px solid #292929;
  bottom: 0;
  gap: 10px;
}

.mobile-filter-cancel,
.mobile-filter-clear,
.mobile-filter-apply {
  border-radius: 999px;
  cursor: pointer;
  font-family: "Oswald", sans-serif;
  font-size: .85rem;
  font-weight: 900;
  padding: 12px 16px;
  text-transform: uppercase;
}

.mobile-filter-cancel {
  background: transparent;
  border: 1px solid #3a3a3a;
  color: #fff;
  flex: 0 0 auto;
  white-space: nowrap;
}

.mobile-filter-clear {
  background: transparent;
  border: 1px solid #e83b3b;
  color: #fff;
  flex: 0 0 auto;
  white-space: nowrap;
}

.mobile-filter-apply {
  background: #e83b3b;
  border: 1px solid #e83b3b;
  color: #fff;
  flex: 0 1 220px;
  margin-left: auto;
  min-width: 0;
  white-space: nowrap;
}

@media (max-width: 360px) {
  .mobile-filter-footer {
    gap: 7px;
    padding-left: 10px;
    padding-right: 10px;
  }

  .mobile-filter-cancel,
  .mobile-filter-clear,
  .mobile-filter-apply {
    font-size: .74rem;
    padding-left: 10px;
    padding-right: 10px;
  }
}

@media (max-width: 760px) {
  .tag-filter {
    padding: 18px;
  }

  .tag-filter-heading {
    display: block;
  }

  .tag-filter-result-count {
    display: inline-block;
    margin-top: 14px;
  }

}
</style>

<style>
.story-filter-mobile-dialog {
  background: #050505;
}

.story-filter-dialog-open .ai-assistant {
  display: none !important;
}
</style>
